import React, { useState } from "react";
import { MatrixDim, MatrixRow, PathKey, StepDef } from "./types";
import { fmtN, screenLabel, SHORT_STEP } from "./format";

// ---- funnel by segment ------------------------------------------------------

// More dimensions land here as they exist on the device (referral code when
// the referral program ships; email campaigns already arrive as utm tags).
export const MATRIX_CHIPS: { key: MatrixDim; label: string }[] = [
  { key: "source", label: "Source" },
  { key: "platform", label: "Platform" },
  { key: "country", label: "Country" },
  { key: "landing", label: "Landing page" },
  { key: "appVersion", label: "App version" },
  { key: "campaign", label: "Campaign" },
];

export function MatrixCard({ matrix, steps }: { matrix: Record<MatrixDim, MatrixRow[]>; steps: { dj: StepDef[]; fan: StepDef[] } }) {
  const [dim, setDim] = useState<MatrixDim>("source");
  const [path, setPath] = useState<PathKey>("dj");
  const rows = matrix[dim] || [];
  const defs = steps[path];
  // keep% = share of the previous column (visitors for the first step);
  // cells shade by keep% relative to the best row in that column.
  const cells = rows.map((r) => {
    const counts = r[path];
    return counts.map((n, i) => {
      const prev = i === 0 ? r.visitors : counts[i - 1];
      return { n, keep: prev > 0 ? (n / prev) * 100 : null };
    });
  });
  const colMax = defs.map((_, i) => Math.max(0, ...cells.map((c) => c[i]?.keep ?? 0)));
  const label = MATRIX_CHIPS.find((c) => c.key === dim)?.label || dim;
  const keyLabel = (k: string) => (dim === "platform" ? (k === "web" ? "Website" : "App") : dim === "landing" ? screenLabel(k) : k);
  return (
    <div className="adm-card gr-block">
      <h3>
        {path === "dj" ? "Talent" : "Fan"} funnel by segment <small>· which channel, platform or country leaks where</small>
        <span className="gr-seg">
          {(["dj", "fan"] as PathKey[]).map((p) => (
            <button key={p} className={path === p ? "on" : ""} onClick={() => setPath(p)}>
              {p === "dj" ? "Talent" : "Fan"}
            </button>
          ))}
        </span>
      </h3>
      <div className="gr-chips">
        {MATRIX_CHIPS.map((c) => (
          <button key={c.key} className={dim === c.key ? "on" : ""} onClick={() => setDim(c.key)}>
            {c.label}
          </button>
        ))}
      </div>
      {rows.length === 0 ? (
        <div className="gr-muted">Nothing in this window yet.</div>
      ) : (
        <div className="gr-scroll-x">
          <table className="gr-table gr-matrix">
            <thead>
              <tr>
                <th>{label}</th>
                <th className="gr-num">Visitors</th>
                {defs.map((d) => (
                  <th className="gr-num" key={d.key}>
                    {SHORT_STEP[d.key] || d.label}
                    <small>% of previous</small>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={r.key}>
                  <td className="gr-key">{keyLabel(r.key)}</td>
                  <td className="gr-num">{fmtN(r.visitors)}</td>
                  {cells[ri].map((c, i) => {
                    const alpha = c.keep != null && colMax[i] > 0 ? 0.06 + 0.3 * (c.keep / colMax[i]) : 0;
                    return (
                      <td className="gr-num gr-heat" key={i} style={alpha ? { background: `rgba(59,130,246,${alpha.toFixed(2)})` } : undefined}>
                        {fmtN(c.n)}
                        <small>{c.keep != null ? `${Math.round(c.keep)}%` : "—"}</small>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="gr-note">
        Darker = better conversion into that step, within its column. Fan accounts carry no device, so the Fan view goes straight
        from "chose fan" to "activated". Signups without a device (backfilled history) sit under "(unknown)".
      </div>
    </div>
  );
}
